/**
 * Streaming chat composable for JarvisGPT.
 *
 * Mirrors EREA's `useChat` shape but consumes the canonical SSE event
 * vocabulary defined in `shared/streaming/events.md` (and emitted by
 * `services/chatbot/app/routers/chat.py`).
 *
 * State is kept **per thread** in `threadStates` so that switching threads (or
 * starting a second conversation) never tears down an in-flight run: each
 * thread's "working" indicator and activity steps live in its own slice and
 * keep updating in the background. The active thread's slice is surfaced via
 * computed refs so consuming components are unchanged.
 *
 * A run can also be **reattached** after a page refresh via
 * `GET /v1/chat/stream/{thread_id}` — the backend keeps the run alive and
 * replays the buffered events so the step list and indicator resume.
 *
 * Event categories handled:
 *   - lifecycle:           keepalive, done, error, warning, no_active_run
 *   - workflow lifecycle:  node_start, node_end
 *   - per-node payloads:   query_resolved, tool_catalog, tags_extracted,
 *                          entities_resolved, route_decided, probe_executing,
 *                          probe_result, filter_built, tool_use, tool_executing,
 *                          tool_progress, tool_result, evidence_scored,
 *                          retry_decision, composer_chunk, usage, phase_change
 *   - chat-state:          text, thinking_start/thinking/thinking_done,
 *                          context_boundary, stream_debug
 */

import { computed, reactive } from 'vue'
import { notifyAuthError } from '@/services/api'
import { useThreads } from './useThreads'
import type {
  ChatItem, ChatMessage, ToolActivity, NodeActivity,
  RouteName, SourceCitation, RawCitation, HistoryThreadMessage, HistoryResponse,
} from '@/types'
import { ROUTE_LABELS } from '@/types'

// Event emitted by the reconnect endpoint when there is no live run for a
// thread; tells the client to stop the indicator and rely on history.
const NO_ACTIVE_RUN = 'no_active_run'

const MAX_RETRIES = 2

// Reconnect budget for a turn whose live stream dropped. The run usually
// outlives the connection, so recovering it is far cheaper (and far less
// confusing) than asking the user to send the message again.
const RESUME_ATTEMPTS = 3
const RESUME_BACKOFF_MS = 400
// Polls of the persisted history once the server reports no live run: the
// checkpoint write can lag the stream by a moment.
const HISTORY_POLLS = 2

// A connection that has gone this long without any frame (keepalives included,
// and the server sends one every 15s) is dead even though the socket is open.
const STALL_TIMEOUT_MS = 90_000
const STALL_CHECK_MS = 15_000

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

// ------- pending-turn markers ----------------------------------------------
// A thread is "pending" from the moment a turn is sent until we observe it
// finish (done/error) or explicitly abort. Persisting this set lets a freshly
// loaded tab (after a refresh) show the "working" indicator immediately and
// know it should reconnect to the in-flight run, instead of rendering a static
// history with no sign that the agent is still working.
const PENDING_STORAGE_KEY = 'jarvis.pendingThreads'

function readPending(): Set<string> {
  try {
    const raw = localStorage.getItem(PENDING_STORAGE_KEY)
    return new Set(raw ? (JSON.parse(raw) as string[]) : [])
  } catch {
    return new Set()
  }
}

function writePending(ids: Set<string>): void {
  try {
    localStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify([...ids]))
  } catch {
    /* localStorage unavailable (private mode / SSR) — best-effort only */
  }
}

function markPending(threadId: string): void {
  const ids = readPending()
  ids.add(threadId)
  writePending(ids)
}

function clearPending(threadId: string): void {
  const ids = readPending()
  if (ids.delete(threadId)) writePending(ids)
}

function isPending(threadId: string): boolean {
  return readPending().has(threadId)
}

// ------- per-thread state ---------------------------------------------------

interface ThreadState {
  items: ChatItem[]
  sources: SourceCitation[]
  isStreaming: boolean
  isThinking: boolean
  currentRoute: RouteName | null
  currentPhase: string | null
  totalCost: number
  totalInputTokens: number
  totalOutputTokens: number
  connectionError: boolean
  lastUserMessage: string | null
  nodeStatus: { node: string | null; detail: string | null }
  streamDebug: Record<string, unknown> | null
  retryCount: number
  // Non-reactive transient handle for aborting this thread's live stream.
  abortController: AbortController | null
  /**
   * The user pressed Stop for this turn. Distinct from a transport drop (which
   * is recoverable) and from a stall abort (which we raise ourselves).
   */
  userAborted: boolean
  /**
   * A recovery loop owns this thread: it may hold no open connection between
   * attempts, but nothing else may start a turn on it. `isStreaming` tracks
   * only the socket, so it goes false during backoff and history polls.
   */
  recovering: boolean
}

function blankState(): ThreadState {
  return {
    items: [],
    sources: [],
    isStreaming: false,
    isThinking: false,
    currentRoute: null,
    currentPhase: null,
    totalCost: 0,
    totalInputTokens: 0,
    totalOutputTokens: 0,
    connectionError: false,
    lastUserMessage: null,
    nodeStatus: { node: null, detail: null },
    streamDebug: null,
    retryCount: 0,
    abortController: null,
    userAborted: false,
    recovering: false,
  }
}

// Keyed by thread_id. A reactive record so newly-added threads are reactive.
const threadStates = reactive<Record<string, ThreadState>>({})

const { activeThreadId, selectThread, upsertThread, fetchHistory } = useThreads()

/** Normalize history rows to user/assistant turns (tool/system shown as assistant). */
function toHistoryMessages(messages: HistoryResponse['messages']): HistoryThreadMessage[] {
  return messages.map(m => ({
    role: m.role === 'user' || m.role === 'assistant' ? m.role : 'assistant',
    content: m.content,
    timestamp: m.timestamp,
    metadata: m.metadata,
  }))
}

function getOrCreateState(threadId: string): ThreadState {
  if (!threadStates[threadId]) {
    threadStates[threadId] = blankState()
  }
  return threadStates[threadId]
}

// ------- active-thread views (module-level so they're shared) ---------------

const activeState = computed<ThreadState | undefined>(() => {
  const id = activeThreadId.value
  return id ? threadStates[id] : undefined
})

const EMPTY_ITEMS: ChatItem[] = []
const EMPTY_SOURCES: SourceCitation[] = []

const items = computed<ChatItem[]>(() => activeState.value?.items ?? EMPTY_ITEMS)
const sources = computed<SourceCitation[]>(() => activeState.value?.sources ?? EMPTY_SOURCES)
// Recovery keeps the working indicator lit: the socket is briefly closed
// between reconnect attempts, but the turn is still in flight.
const isStreaming = computed(
  () => (activeState.value?.isStreaming || activeState.value?.recovering) ?? false,
)
const isThinking = computed(() => activeState.value?.isThinking ?? false)
const currentRoute = computed(() => activeState.value?.currentRoute ?? null)
const currentPhase = computed(() => activeState.value?.currentPhase ?? null)
const totalCost = computed(() => activeState.value?.totalCost ?? 0)
const totalInputTokens = computed(() => activeState.value?.totalInputTokens ?? 0)
const totalOutputTokens = computed(() => activeState.value?.totalOutputTokens ?? 0)
const connectionError = computed(() => activeState.value?.connectionError ?? false)
const nodeStatus = computed(() => activeState.value?.nodeStatus ?? { node: null, detail: null })
const streamDebug = computed(() => activeState.value?.streamDebug ?? null)

// ------- helpers ------------------------------------------------------------

function makeMsg(role: 'user' | 'assistant', content: string, id?: string): ChatMessage {
  return { id: id || crypto.randomUUID(), role, content, timestamp: Date.now() }
}

function makeTool(
  id: string, tool: string, status: ToolActivity['status'],
  detail: string, label?: string,
): ToolActivity {
  return {
    id: id || crypto.randomUUID(),
    type: 'tool',
    tool,
    status,
    label: label || tool,
    detail,
    timestamp: Date.now(),
  }
}

function makeNode(
  node: string, status: NodeActivity['status'], detail: string,
  meta?: Record<string, unknown>,
): NodeActivity {
  return {
    id: `node-${node}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    type: 'node',
    node,
    status,
    detail,
    meta,
    timestamp: Date.now(),
  }
}

function shortenString(s: unknown, max = 80): string {
  const str = typeof s === 'string' ? s : JSON.stringify(s)
  if (str.length <= max) return str
  return str.slice(0, max - 1) + '…'
}

/**
 * Whether an event advances the reconnect cursor.
 *
 * The cursor indexes the run's server-side replay buffer (`RunSession.events`),
 * so events the server mints per subscriber — keepalives, `no_active_run`, and
 * the `subscriber_limit` refusal — are never part of it and must not count.
 */
function countsTowardCursor(event: string, data: Record<string, unknown>): boolean {
  if (event === 'keepalive' || event === NO_ACTIVE_RUN) return false
  if (event === 'error' && data.code === 'subscriber_limit') return false
  return true
}

/**
 * Parse an SSE byte stream, invoking `onEvent(event, data)` for each complete
 * `event:`/`data:` pair. `onActivity` fires on every chunk (for stall checks).
 * Shared by `sendMessage` (POST stream) and `reattach` (reconnect GET stream).
 */
async function consumeSSE(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onEvent: (event: string, data: Record<string, unknown>) => void,
  onActivity?: () => void,
): Promise<void> {
  const decoder = new TextDecoder()
  let buffer = ''
  let currentEvent: string | null = null

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    onActivity?.()
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop()!

    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEvent = line.slice(7).trim()
      } else if (line.startsWith('data: ') && currentEvent) {
        let data: Record<string, unknown>
        try {
          data = JSON.parse(line.slice(6))
        } catch {
          console.warn('[useChat] malformed SSE data:', line.slice(6, 100))
          currentEvent = null
          continue
        }
        try {
          onEvent(currentEvent, data)
        } catch (err) {
          console.error('[useChat] handler error for', currentEvent, err)
        }
        currentEvent = null
      }
    }
  }
}

/**
 * Build a stateful SSE event handler bound to one thread's state slice. Keeps
 * per-stream locals (assistant bubble id, accumulated text, active tools) so
 * the same logic can drive a fresh send or a reconnect replay.
 */
function createDispatcher(state: ThreadState) {
  let assistantId = ''
  let assistantText = ''
  let receivedDone = false
  let receivedServerError = false
  const activeTools: Record<string, string> = {}

  function ensureAssistantBubble() {
    if (!assistantId) {
      assistantId = crypto.randomUUID()
      state.items.push(makeMsg('assistant', '', assistantId))
    }
  }

  function findAssistant(): ChatMessage | undefined {
    return state.items.find(i => !('type' in i) && i.id === assistantId) as ChatMessage | undefined
  }

  function findTool(id: string): ToolActivity | undefined {
    return state.items.find(
      i => 'type' in i && i.type === 'tool' && i.id === id,
    ) as ToolActivity | undefined
  }

  function findNode(node: string): NodeActivity | undefined {
    for (let i = state.items.length - 1; i >= 0; i--) {
      const it = state.items[i]
      if ('type' in it && it.type === 'node' && (it as NodeActivity).node === node) {
        return it as NodeActivity
      }
    }
    return undefined
  }

  function markStaleTools() {
    for (const it of state.items) {
      if ('type' in it && it.type === 'tool' && (it as ToolActivity).status === 'executing') {
        (it as ToolActivity).status = 'error'
        ;(it as ToolActivity).detail =
          ((it as ToolActivity).detail || '') + ' — interrupted'
      }
    }
  }

  function renderErrorBubble(message: string, retryable: boolean) {
    markStaleTools()
    ensureAssistantBubble()
    const a = findAssistant()
    if (a) {
      a.content = message
      a.error = true
      a.retryable = retryable
    }
    // Only surface the "Connection lost — Retry" banner for genuinely
    // retryable/transport failures. Clean, non-retryable refusals (e.g.
    // credit_exhausted, expired session) already render in the bubble; a
    // retry won't help, so the banner would be misleading.
    state.connectionError = retryable
  }

  /** A terminal verdict from the server: the turn is resolved, don't resume. */
  function setErrorBubble(message: string, retryable = true) {
    receivedServerError = true
    renderErrorBubble(message, retryable)
  }

  /**
   * The connection failed, not the run. Rendered without marking the turn
   * resolved, so a reconnect (now or after a refresh) is still allowed.
   */
  function setTransportErrorBubble(message: string) {
    renderErrorBubble(message, true)
  }

  function removeEmptyAssistantBubble() {
    const a = findAssistant()
    if (a && !a.content) {
      const idx = state.items.findIndex(i => i.id === assistantId)
      if (idx >= 0) state.items.splice(idx, 1)
    }
  }

  function mergeSources(incoming: SourceCitation[]) {
    const seen = new Set(state.sources.map(s => s.id || s.url || s.title))
    for (const s of incoming) {
      const key = s.id || s.url || s.title
      if (!seen.has(key)) {
        seen.add(key)
        state.sources.push(s)
      }
    }
  }

  function handleSSEEvent(event: string, data: Record<string, unknown>) {
    switch (event) {
      // ---- lifecycle ----
      case 'keepalive':
        return
      case 'done': {
        receivedDone = true
        const finalMessage = (data.message as string) || ''
        const runId = (data.run_id as string | undefined)
          || ((data.metadata as Record<string, unknown> | undefined)?.run_id as string | undefined)
        const meta = data.metadata as Record<string, unknown> | undefined
        const rawCitations = (data.citations as RawCitation[] | undefined)
          ?? (meta?.citations as RawCitation[] | undefined)
        if (import.meta.env.DEV) {
          const withTier = Array.isArray(rawCitations)
            ? rawCitations.filter((c) => c?.risk_tier != null).length
            : 0
          // eslint-disable-next-line no-console
          console.debug('[citations] done event:', {
            count: Array.isArray(rawCitations) ? rawCitations.length : 0,
            withRiskTier: withTier,
            sample: Array.isArray(rawCitations) ? rawCitations.slice(0, 5) : rawCitations,
          })
        }
        if (finalMessage) {
          ensureAssistantBubble()
          assistantText = finalMessage
          const a = findAssistant()
          if (a) {
            a.content = finalMessage
            if (runId) a.runId = runId
            if (Array.isArray(rawCitations)) a.citations = rawCitations
            if (meta?.awaiting_reply === true) a.awaitingReply = true
          }
        }
        const tid = data.thread_id as string | undefined
        // Don't yank the user's view to this thread if they've navigated away;
        // only adopt it when nothing is selected yet (brand-new conversation).
        if (tid && activeThreadId.value == null) {
          selectThread(tid)
        }
        if (tid) {
          const now = new Date().toISOString()
          upsertThread({
            thread_id: tid,
            created_at: now,
            updated_at: now,
            name: shortenString(state.lastUserMessage ?? '', 60),
            message_count: state.items.filter(i => !('type' in i)).length,
          })
        }
        return
      }
      case 'error': {
        const friendly = (data.message || data.detail || data.error
          || 'Something went wrong while generating a response. Please try again.') as string
        // `subscriber_limit` refuses this connection, not the run: it is minted
        // per subscriber and never enters the replay buffer, so the turn has to
        // stay recoverable instead of counting as the server's verdict.
        if (data.code === 'subscriber_limit') {
          setTransportErrorBubble(friendly)
          return
        }
        const retryable = data.retryable !== false
        setErrorBubble(friendly, retryable)
        return
      }
      case 'warning': {
        ensureAssistantBubble()
        const a = findAssistant()
        if (a) a.content += `\n\n⚠️ ${data.warning as string}`
        return
      }

      // ---- workflow node lifecycle ----
      case 'node_start': {
        const node = data.node as string
        state.nodeStatus = { node, detail: `${node} running…` }
        return
      }
      case 'node_end': {
        const node = data.node as string
        const elapsed = (data.elapsed_ms as number) || 0
        const summary = (data.summary as string) || ''
        const last = findNode(node)
        if (last && last.status === 'running') {
          last.status = data.error ? 'error' : 'complete'
          if (summary) last.detail = summary
          else last.detail += ` (${elapsed}ms)`
        }
        if (state.nodeStatus.node === node) {
          state.nodeStatus = { node: null, detail: null }
        }
        return
      }

      // ---- per-node payloads ----
      case 'query_resolved': {
        const cleaned = (data.cleaned_query as string) || (data.query as string) || ''
        if (cleaned) {
          state.items.push(makeNode('query_prep', 'complete',
            `Query understood: "${shortenString(cleaned, 100)}"`))
        }
        return
      }
      case 'tool_catalog': {
        const count = (data.count as number) ?? (Array.isArray(data.tools) ? (data.tools as unknown[]).length : 0)
        state.items.push(makeNode('tool_registry', 'complete',
          `Loaded ${count} tool${count === 1 ? '' : 's'}`))
        return
      }
      case 'tags_extracted': {
        const tags = (data.tags as Record<string, unknown>) || {}
        const counts = Object.entries(tags)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.length : 1}`)
          .join(', ')
        state.items.push(makeNode('tag_extraction', 'complete',
          counts ? `Tags: ${counts}` : 'No tags extracted'))
        return
      }
      case 'entities_resolved': {
        const resolved = (data.resolved as Record<string, unknown>) || {}
        const summary = Object.keys(resolved).length
          ? Object.entries(resolved).map(([k, v]) => `${k}: ${shortenString(v, 30)}`).join(', ')
          : (data.summary as string) || 'No entities resolved'
        state.items.push(makeNode('resolver', 'complete', `Entities: ${summary}`))
        return
      }
      case 'route_decided': {
        const route = (data.route as RouteName) || null
        const confidence = (data.confidence as number) ?? null
        state.currentRoute = route
        const label = route ? (ROUTE_LABELS[route] || route) : 'unknown'
        const conf = confidence !== null ? ` (${Math.round(confidence * 100)}%)` : ''
        state.items.push(makeNode('router', 'complete',
          `Route: ${label}${conf}`, { route, confidence }))
        return
      }
      case 'probe_executing': {
        state.items.push(makeNode('probe_executor', 'running',
          `Probing: ${shortenString(data.tool || data.query || '?', 60)}`))
        return
      }
      case 'probe_result': {
        const last = findNode('probe_executor')
        const count = (data.result_count as number) ?? null
        const detail = count !== null
          ? `Probe complete — ${count} result${count === 1 ? '' : 's'}`
          : `Probe complete`
        if (last && last.status === 'running') {
          last.status = 'complete'
          last.detail = detail
        } else {
          state.items.push(makeNode('probe_executor', 'complete', detail))
        }
        return
      }
      case 'filter_built': {
        const odata = (data.odata as string) || (data.summary as string) || ''
        state.items.push(makeNode('retrieval_planner', 'complete',
          `Filter built: ${shortenString(odata, 100)}`))
        return
      }
      case 'tool_use': {
        const id = (data.id as string) || crypto.randomUUID()
        const tool = (data.tool as string) || 'tool'
        activeTools[id] = tool
        state.items.push(makeTool(id, tool, 'executing', `${tool}…`, tool))
        return
      }
      case 'tool_executing': {
        const id = data.id as string
        const tool = (data.tool as string) || activeTools[id] || 'tool'
        const input = (data.input as string) || (data.input_summary as string) || ''
        const t = findTool(id)
        if (t) {
          t.status = 'executing'
          t.detail = input ? `${tool}: ${shortenString(input, 80)}` : `${tool}…`
        }
        return
      }
      case 'tool_progress': {
        const id = data.id as string
        const elapsed = (data.elapsed_seconds as number) || 0
        const tool = (data.tool as string) || activeTools[id] || 'tool'
        const t = findTool(id)
        if (t) t.detail = `${tool} — running (${elapsed}s)`
        return
      }
      case 'tool_result': {
        const id = data.id as string
        const tool = (data.tool as string) || activeTools[id] || 'tool'
        const count = (data.result_count as number) ?? null
        const summary = (data.result_summary as string)
          || (count !== null ? `${tool}: ${count} result${count === 1 ? '' : 's'}` : `${tool} done`)
        const t = findTool(id)
        if (t) {
          t.status = 'complete'
          t.detail = summary
          t.resultCount = count
        }
        delete activeTools[id]

        const newSources = data.sources as SourceCitation[] | undefined
        if (Array.isArray(newSources)) mergeSources(newSources)
        return
      }
      case 'evidence_scored': {
        const kept = (data.kept as number) ?? null
        const total = (data.total as number) ?? null
        const detail = kept !== null && total !== null
          ? `Kept ${kept}/${total} evidence chunks`
          : 'Evidence scored'
        state.items.push(makeNode('evidence_processor', 'complete', detail))
        return
      }
      case 'retry_decision': {
        const willRetry = !!data.retry
        const reason = (data.reason as string) || ''
        state.items.push(makeNode('evidence_processor', willRetry ? 'running' : 'complete',
          willRetry ? `Retrying with relaxed filters — ${reason}` : `No retry — ${reason}`))
        return
      }
      case 'composer_chunk': {
        ensureAssistantBubble()
        const chunk = (data.delta as string) || (data.text as string) || (data.chunk as string) || ''
        if (chunk) {
          assistantText += chunk
          const a = findAssistant()
          if (a) a.content = assistantText
        }
        return
      }
      case 'usage': {
        if (typeof data.total_cost === 'number') state.totalCost = data.total_cost
        if (typeof data.total_input_tokens === 'number') state.totalInputTokens = data.total_input_tokens
        if (typeof data.total_output_tokens === 'number') state.totalOutputTokens = data.total_output_tokens
        return
      }
      case 'phase_change':
        state.currentPhase = (data.phase as string) || null
        return

      // ---- chat-state ----
      case 'text': {
        ensureAssistantBubble()
        const t = (data.text as string) || ''
        assistantText += t
        const a = findAssistant()
        if (a) a.content = assistantText
        return
      }
      case 'thinking_start':
        state.isThinking = true
        return
      case 'thinking':
        return
      case 'thinking_done':
        state.isThinking = false
        return
      case 'agent_status':
      case 'agent_event': {
        const detail = (data.detail as string) || (data.message as string) || ''
        if (detail) {
          state.items.push(makeNode((data.agent as string) || 'agent', 'complete', detail))
        }
        return
      }
      case 'context_boundary':
        return
      case 'stream_debug':
        state.streamDebug = data
        return

      default:
        // Forward-compatible: ignore unknown events instead of crashing.
        return
    }
  }

  return {
    handleSSEEvent,
    setErrorBubble,
    setTransportErrorBubble,
    markStaleTools,
    ensureAssistantBubble,
    findAssistant,
    removeEmptyAssistantBubble,
    get receivedDone() { return receivedDone },
    get receivedServerError() { return receivedServerError },
    /** Resolved by the server, either way — nothing left to recover. */
    get settled() { return receivedDone || receivedServerError },
  }
}

type Dispatcher = ReturnType<typeof createDispatcher>

/** How a reconnect attempt ended. `failed` means the answer is still out there. */
type ReattachOutcome = 'streamed' | 'no_active_run' | 'failed'

interface ReattachResult {
  outcome: ReattachOutcome
  /** Replay index reached, so a follow-up reconnect resumes without repeats. */
  cursor: number
}

/** A turn that ended in a retryable error may have been answered server-side. */
function needsHistoryRefresh(state: ThreadState): boolean {
  if (state.connectionError) return true
  return state.items.some(
    i => !('type' in i) && (i as ChatMessage).role === 'assistant' && (i as ChatMessage).error,
  )
}

/** Drop the interrupted assistant bubble so a fresh attempt replaces it. */
function dropTrailingErrorBubble(state: ThreadState): void {
  for (let i = state.items.length - 1; i >= 0; i--) {
    const it = state.items[i]
    if ('type' in it) continue
    const msg = it as ChatMessage
    if (msg.role === 'assistant' && msg.error) state.items.splice(i, 1)
    break
  }
}

// ------- composable ---------------------------------------------------------

export function useChat() {
  async function abortStream() {
    const state = activeState.value
    const threadId = activeThreadId.value
    if (!state || !threadId) return
    try {
      const response = await fetch(`/v1/chat/cancel/${encodeURIComponent(threadId)}`, {
        method: 'POST', credentials: 'include',
      })
      if (!response.ok) throw new Error('Unable to stop the report')
      // Keep listening for the terminal cancellation event and usage cleanup.
      // Flagged only once the server has accepted the stop, so a failed
      // cancellation still leaves the turn recoverable.
      state.userAborted = true
    } catch (error) {
      console.error('[useChat] cancellation failed:', error)
      state.connectionError = true
    }
  }

  function isThreadStreaming(threadId: string): boolean {
    const state = threadStates[threadId]
    return (state?.isStreaming || state?.recovering) ?? false
  }

  function threadHasItems(threadId: string): boolean {
    return (threadStates[threadId]?.items.length ?? 0) > 0
  }

  /**
   * Hydrate a thread's message list from persisted history. Workflow activity
   * (tool_use, route_decided, …) lives only in the live stream and is not
   * replayed here; reconnect (`reattach`) replays it for in-flight runs. Never
   * clobbers a thread that is currently streaming in this tab.
   */
  function hydrateFromHistory(
    threadId: string,
    messages: HistoryThreadMessage[],
    metadata?: Record<string, unknown>,
  ) {
    const state = getOrCreateState(threadId)
    if (state.isStreaming) return
    // Seed the per-thread Foresight cost badge from the history metadata
    // (backend pulls the cumulative project totals from Vision since a plain
    // history fetch has no `usage` SSE event). Same keys the streaming `usage`
    // event uses, so the badge looks identical whether live or reloaded.
    if (metadata) {
      if (typeof metadata.total_cost === 'number') state.totalCost = metadata.total_cost
      if (typeof metadata.total_input_tokens === 'number') state.totalInputTokens = metadata.total_input_tokens
      if (typeof metadata.total_output_tokens === 'number') state.totalOutputTokens = metadata.total_output_tokens
    }
    const newItems: ChatItem[] = []
    for (const msg of messages) {
      if (msg.role === 'user' || msg.role === 'assistant') {
        const item = makeMsg(msg.role, msg.content)
        const route = (msg.metadata?.route as RouteName | undefined) ?? null
        if (route) item.route = route
        // Threads saved before `awaiting_reply` existed only carry the route.
        if (msg.metadata?.awaiting_reply === true || route === 'travel_clarify') item.awaitingReply = true
        const runId = msg.metadata?.run_id as string | undefined
        if (runId) item.runId = runId
        // Restore the per-answer citations the composer persisted on the
        // assistant message so risk tiers survive a page refresh (not just the
        // live `done` event). Mirrors the streaming handler above.
        const citations = msg.metadata?.citations as RawCitation[] | undefined
        if (Array.isArray(citations)) item.citations = citations
        newItems.push(item)
      }
    }
    state.items = newItems
  }

  /**
   * Reconnect to a run for `threadId` (e.g. after a page refresh, when
   * re-opening a thread that is still working, or when a live stream dropped
   * mid-answer). Streams the reconnect endpoint from `cursor` so the server
   * replays only what this client missed, then tails live events.
   *
   * Pass the dropped turn's `dispatcher` to keep appending to the same
   * assistant bubble; omit it to render the replay from scratch.
   */
  async function reattach(
    threadId: string,
    opts: { cursor?: number; dispatcher?: Dispatcher } = {},
  ): Promise<ReattachResult> {
    const state = getOrCreateState(threadId)
    let cursor = opts.cursor ?? 0
    // Only bail if there's already a *live* connection for this thread. We must
    // not key off `isStreaming` here: a freshly restored thread may have an
    // optimistic indicator set (from a persisted pending marker) that we still
    // need to reconnect behind.
    if (state.abortController) return { outcome: 'streamed', cursor }

    const controller = new AbortController()
    state.abortController = controller
    state.isStreaming = true
    state.connectionError = false
    const dispatcher = opts.dispatcher ?? createDispatcher(state)
    let sawNoActiveRun = false

    try {
      const query = cursor > 0 ? `?cursor=${cursor}` : ''
      const res = await fetch(
        `/v1/chat/stream/${encodeURIComponent(threadId)}${query}`,
        { credentials: 'include', signal: controller.signal },
      )
      if (res.status === 401) notifyAuthError()
      if (!res.ok || !res.body) return { outcome: 'failed', cursor }

      const reader = res.body.getReader()
      // A reconnect needs the same watchdog as a live send: without it a socket
      // that is open but silent parks the turn in "working" forever.
      let lastEventTime = Date.now()
      const stallCheck = setInterval(() => {
        if (Date.now() - lastEventTime > STALL_TIMEOUT_MS) {
          reader.cancel().catch(() => { /* already closed */ })
          controller.abort()
        }
      }, STALL_CHECK_MS)
      try {
        await consumeSSE(
          reader,
          (event, data) => {
            if (event === NO_ACTIVE_RUN) {
              sawNoActiveRun = true
              return
            }
            if (countsTowardCursor(event, data)) cursor++
            dispatcher.handleSSEEvent(event, data)
          },
          () => { lastEventTime = Date.now() },
        )
      } finally {
        clearInterval(stallCheck)
      }

      if (sawNoActiveRun) return { outcome: 'no_active_run', cursor }
      if (dispatcher.settled) return { outcome: 'streamed', cursor }
      // The reconnect itself dropped before a terminal event. Clear spinners so
      // the UI doesn't hang; the caller decides whether to try again.
      dispatcher.markStaleTools()
      return { outcome: 'failed', cursor }
    } catch (e: unknown) {
      if (!controller.signal.aborted) {
        console.error('[useChat] reattach error:', e)
        dispatcher.markStaleTools()
      }
      return { outcome: 'failed', cursor }
    } finally {
      if (state.abortController === controller) state.abortController = null
      state.isStreaming = false
      state.isThinking = false
      state.nodeStatus = { node: null, detail: null }
      // Only a terminal event resolves the turn here. `no_active_run` is left
      // to the caller, which first confirms the answer is in the history —
      // otherwise the marker is still the only route back to this run.
      if (dispatcher.settled) clearPending(threadId)
    }
  }

  /**
   * Replace a thread's items with the persisted history, but only once the
   * server has recorded an assistant reply for the latest user turn. The
   * checkpoint write can lag the stream, and a shorter history must never
   * overwrite the turn the user is waiting on.
   */
  async function hydrateAnsweredTurn(threadId: string): Promise<boolean> {
    const history = await fetchHistory(threadId)
    if (!history) return false
    const msgs = toHistoryMessages(history.messages)
    const lastUser = msgs.map(m => m.role).lastIndexOf('user')
    if (lastUser < 0) return false
    if (!msgs.slice(lastUser + 1).some(m => m.role === 'assistant')) return false

    const state = getOrCreateState(threadId)
    // The history has to answer the turn we're waiting on, not an earlier one:
    // a reply to the previous question would otherwise look like success and
    // retire this turn's pending marker.
    const awaiting = state.lastUserMessage
    if (awaiting && msgs[lastUser].content.trim() !== awaiting.trim()) return false
    // `hydrateFromHistory` refuses to clobber a live stream, so a reconnect that
    // started underneath us means nothing was written and the caller must not
    // treat the turn as collected.
    if (state.isStreaming) return false

    hydrateFromHistory(threadId, msgs, history.metadata)
    state.connectionError = false
    return true
  }

  /**
   * Recover a turn whose live stream dropped. The run almost always outlives
   * the connection, so reconnect from where we left off; if it has already
   * finished and been swept from the registry, read the answer from history.
   */
  async function resumeAfterDrop(
    threadId: string,
    dispatcher: Dispatcher,
    startCursor: number,
  ): Promise<void> {
    const state = getOrCreateState(threadId)
    let cursor = startCursor
    // Hold the thread for the whole loop: between attempts there is no open
    // connection, and a second turn starting here would race this one onto the
    // same bubble and the same pending marker.
    state.recovering = true

    try {
      for (let attempt = 1; attempt <= RESUME_ATTEMPTS; attempt++) {
        if (attempt > 1) await delay(RESUME_BACKOFF_MS * (attempt - 1))
        if (state.userAborted) return

        const result = await reattach(threadId, { cursor, dispatcher })
        cursor = result.cursor
        if (result.outcome === 'streamed') return

        if (result.outcome === 'no_active_run') {
          // Finished and swept, or never registered. Either way no further
          // events are coming, so the checkpoint is the only place the answer
          // can be.
          for (let poll = 1; poll <= HISTORY_POLLS; poll++) {
            if (await hydrateAnsweredTurn(threadId)) {
              clearPending(threadId)
              return
            }
            if (poll < HISTORY_POLLS) await delay(RESUME_BACKOFF_MS)
          }
          break
        }
      }

      // Out of attempts, or the run vanished without a persisted answer. The
      // pending marker stays either way, so re-opening this thread (or
      // reloading the tab) makes one more attempt to collect the answer.
      dispatcher.setTransportErrorBubble(
        'The connection was interrupted before the answer finished. Please try again.',
      )
    } finally {
      state.recovering = false
    }
  }

  /**
   * Open a thread for viewing: switch to it, and (unless it's already loaded or
   * streaming live in this tab) hydrate its history and reconnect to any
   * in-flight run so the working indicator + step list resume. If no live run
   * exists (e.g. it finished between the history fetch and the reconnect), the
   * history is re-fetched so the final answer is never missed.
   */
  async function openThread(threadId: string): Promise<void> {
    selectThread(threadId)
    if (isThreadStreaming(threadId)) return

    const state = getOrCreateState(threadId)
    const pending = isPending(threadId)
    if (threadHasItems(threadId)) {
      // Already loaded and resolved in this tab — nothing to re-read. A pending
      // marker means the opposite, so it overrides: that turn is still owed an
      // answer however complete the thread looks.
      if (!pending && !needsHistoryRefresh(state)) return
      // An interrupted turn left a retryable error bubble. The answer it never
      // received may have been persisted since, so look again before falling
      // back to a reconnect.
      if (await hydrateAnsweredTurn(threadId)) {
        clearPending(threadId)
        return
      }
    } else {
      const history = await fetchHistory(threadId)
      if (history) hydrateFromHistory(threadId, toHistoryMessages(history.messages), history.metadata)
    }

    if (!pending) {
      // Nothing was in flight; the history fetch above is the whole job.
      await reattach(threadId)
      return
    }

    // A turn was in flight (e.g. before a page refresh). Show the working
    // indicator immediately so it never looks idle, drop the interrupted bubble
    // so the recovered answer doesn't stack underneath it, then run the same
    // multi-attempt recovery a dropped live stream gets.
    state.isStreaming = true
    dropTrailingErrorBubble(state)
    const dispatcher = createDispatcher(state)
    await resumeAfterDrop(threadId, dispatcher, 0)
    dispatcher.removeEmptyAssistantBubble()
  }

  async function sendMessage(
    text: string,
    threadIdOverride?: string | null,
    isRetry = false,
  ): Promise<void> {
    const trimmed = text.trim()
    if (!trimmed) return

    // Resolve the thread id for this turn. For a brand-new conversation we mint
    // the id client-side (the backend honours a supplied thread_id) so the
    // thread shows up in the sidebar with a live "thinking" indicator right
    // away, instead of only appearing after `done` + a manual refresh.
    let turnThreadId = threadIdOverride ?? activeThreadId.value ?? null
    const isNewThread = !turnThreadId
    if (isNewThread) {
      turnThreadId = crypto.randomUUID()
      selectThread(turnThreadId)
    }

    const state = getOrCreateState(turnThreadId!)
    if (state.isStreaming || state.recovering) return

    state.lastUserMessage = trimmed
    if (!isRetry) state.retryCount = 0
    state.connectionError = false
    state.userAborted = false
    // A retry re-uses the user bubble that is already on screen.
    const lastMsg = [...state.items].reverse().find(i => !('type' in i)) as ChatMessage | undefined
    if (!(isRetry && lastMsg?.role === 'user' && lastMsg.content === trimmed)) {
      state.items.push(makeMsg('user', trimmed))
    }

    if (isNewThread && turnThreadId) {
      const now = new Date().toISOString()
      upsertThread({
        thread_id: turnThreadId,
        created_at: now,
        updated_at: now,
        name: shortenString(trimmed, 60),
        message_count: 1,
      })
    }

    state.isStreaming = true
    markPending(turnThreadId!)
    const controller = new AbortController()
    state.abortController = controller
    const signal = controller.signal
    const dispatcher = createDispatcher(state)

    let lastEventTime = Date.now()
    let stallCheck: ReturnType<typeof setInterval> | undefined
    let cursor = 0
    let stalled = false

    try {
      const res = await fetch('/v1/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        signal,
        body: JSON.stringify({
          message: trimmed,
          thread_id: turnThreadId ?? undefined,
          stream: true,
        }),
      })

      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({ detail: res.statusText }))
        let msg: string
        let retryable = true
        if (res.status === 429) {
          msg = err.message || err.detail || 'The assistant is experiencing high demand right now. Please wait a moment and try again.'
        } else if (res.status === 401) {
          msg = err.message || err.detail || 'Your session may have expired. Please sign in again and retry.'
          retryable = false
        } else {
          msg = err.message || err.detail || 'Something went wrong while contacting the assistant. Please try again.'
        }
        dispatcher.setErrorBubble(msg, retryable)
        if (res.status === 401) notifyAuthError()
        throw new Error(msg)
      }

      const reader = res.body.getReader()

      // The server sends a keepalive every 15s, so a window this long with no
      // bytes at all means the connection is dead rather than the run slow.
      // Stop reading and hand over to the reconnect path; the run itself is
      // left alone (no cancellation), because it is still producing the answer.
      stallCheck = setInterval(() => {
        if (Date.now() - lastEventTime > STALL_TIMEOUT_MS && !dispatcher.settled) {
          stalled = true
          // Cancel the reader, not just the request: that ends the read loop
          // deterministically and hands over to the reconnect path. The run
          // itself is never cancelled — it is still producing the answer.
          reader.cancel().catch(() => { /* already closed */ })
          controller.abort()
        }
      }, STALL_CHECK_MS)

      await consumeSSE(
        reader,
        (event, data) => {
          if (countsTowardCursor(event, data)) cursor++
          dispatcher.handleSSEEvent(event, data)
        },
        () => { lastEventTime = Date.now() },
      )
    } catch (e: unknown) {
      // A stall abort and a user stop are both expected; anything else is a
      // genuine transport failure. All three are handled after the `finally`.
      if (!stalled && !state.userAborted && e instanceof Error) {
        console.error('[useChat] stream error:', e.message)
      }
    } finally {
      if (stallCheck) clearInterval(stallCheck)
      if (state.abortController === controller) state.abortController = null
      state.isStreaming = false
      state.isThinking = false
      state.nodeStatus = { node: null, detail: null }
    }

    // Resolved by the server, or stopped on purpose: the turn is finished.
    if (dispatcher.settled || state.userAborted) {
      dispatcher.removeEmptyAssistantBubble()
      clearPending(turnThreadId!)
      return
    }

    // EOF without a terminal event, a thrown read, or a stall. The run is very
    // likely still alive on the server (or has just finished), so go get it
    // rather than making the user send the message again.
    dispatcher.markStaleTools()
    await resumeAfterDrop(turnThreadId!, dispatcher, cursor)
    dispatcher.removeEmptyAssistantBubble()
  }

  /**
   * Retry the last user turn. The previous attempt may only have lost its
   * stream while the run kept going, so reconnect or re-read the history first
   * and resend only when the turn really was never answered.
   */
  async function retryLastMessage(): Promise<void> {
    const state = activeState.value
    const threadId = activeThreadId.value
    if (!state || !threadId || !state.lastUserMessage) return
    if (state.isStreaming || state.abortController || state.recovering) return

    // Every route from here renders its own result, so the interrupted bubble
    // goes now rather than sitting above a second answer.
    dropTrailingErrorBubble(state)

    if (isPending(threadId)) {
      const { outcome } = await reattach(threadId)
      if (outcome === 'streamed') return
      if (outcome === 'no_active_run') clearPending(threadId)
    }
    if (await hydrateAnsweredTurn(threadId)) {
      clearPending(threadId)
      return
    }

    if (state.retryCount >= MAX_RETRIES) {
      // Nothing to collect and no attempts left. Put the interrupted bubble
      // back alongside the Retry banner, so the turn doesn't silently lose both
      // its explanation and its answer.
      state.connectionError = true
      createDispatcher(state).setTransportErrorBubble(
        'The connection was interrupted before the answer finished. Please try again.',
      )
      return
    }
    state.retryCount++
    state.connectionError = false
    await sendMessage(state.lastUserMessage, threadId, true)
  }

  return {
    items,
    sources,
    isStreaming,
    isThinking,
    currentRoute,
    currentPhase,
    totalCost,
    totalInputTokens,
    totalOutputTokens,
    connectionError,
    nodeStatus,
    streamDebug,
    sendMessage,
    retryLastMessage,
    abortStream,
    reattach,
    openThread,
    hydrateFromHistory,
    isThreadStreaming,
    threadHasItems,
  }
}
